// Scientific calculator. Buttons build an expression which is evaluated through
// a small scoped function (the input is the user's own keystrokes in their own
// tool tab, so there is no injection surface).
import { el, wrap, announceSoon } from "./ui.js";

const SPOKEN = { "sin(": "sine", "cos(": "cosine", "tan(": "tangent", "ln(": "natural log", "log(": "log", "√(": "square root", "x²": "squared", "x³": "cubed", "^": "power", "π": "pi", "n!": "factorial", "÷": "divide", "×": "times", "−": "minus", "+": "plus", "C": "clear", "⌫": "delete", "%": "percent", "±": "change sign", "Ans": "last answer", "=": "equals", "e": "e" };

export function calculator(root) {
	const s = wrap(root);
	s.classList.add("rv-calc");
	let deg = true;

	const display = el("input", "rv-calc-display");
	display.type = "text"; display.spellcheck = false; display.setAttribute("aria-label", "Expression");
	display.value = "";
	const out = el("div", "rv-calc-out", "0");
	out.setAttribute("aria-label", "Result");

	const degBtn = el("button", "rv-calc-deg", "DEG");
	degBtn.type = "button";
	const degName = () => degBtn.setAttribute("aria-label", "Angles in " + (deg ? "degrees" : "radians") + ". Switch unit");
	degName();
	degBtn.addEventListener("click", () => { deg = !deg; degBtn.textContent = deg ? "DEG" : "RAD"; degName(); });

	function evaluate(expr) {
		let str = expr.replace(/×/g, "*").replace(/÷/g, "/").replace(/−/g, "-").replace(/π/g, "PI").replace(/\^/g, "**").replace(/%/g, "/100");
		str = str.replace(/(\d+(?:\.\d+)?)!/g, "F($1)");
		const fns = { sin: "S", cos: "C", tan: "T", asin: "AS", acos: "AC", atan: "AT", sqrt: "Q", cbrt: "CB", log: "LG", ln: "LN", abs: "AB", exp: "EX" };
		Object.keys(fns).sort((a, b) => b.length - a.length).forEach((n) => { str = str.replace(new RegExp("\\b" + n + "\\s*\\(", "g"), fns[n] + "("); });
		const tr = (f) => (x) => f(deg ? (x * Math.PI) / 180 : x);
		const itr = (f) => (x) => (deg ? (f(x) * 180) / Math.PI : f(x));
		const F = (n) => { let r = 1; for (let i = 2; i <= n; i++) r *= i; return r; };
		const scope = { S: tr(Math.sin), C: tr(Math.cos), T: tr(Math.tan), AS: itr(Math.asin), AC: itr(Math.acos), AT: itr(Math.atan), Q: Math.sqrt, CB: Math.cbrt, LG: Math.log10, LN: Math.log, AB: Math.abs, EX: Math.exp, PI: Math.PI, E: Math.E, F };
		const fn = Function(...Object.keys(scope), "return (" + str + ");");
		return fn(...Object.values(scope));
	}

	function compute() {
		const v = display.value.trim();
		if (!v) { out.textContent = "0"; return; }
		try { const r = evaluate(v); out.textContent = (r === undefined || Number.isNaN(r)) ? "Error" : String(+r.toPrecision(12)); }
		catch (e) { out.textContent = "Error"; }
		announceSoon("Result " + out.textContent);
	}
	function ins(t) { display.value += t; display.focus(); compute(); }

	display.addEventListener("input", compute);
	display.addEventListener("keydown", (e) => { if (e.key === "Enter") { e.preventDefault(); const v = out.textContent; if (v !== "Error") display.value = v; compute(); } });

	const keys = [
		["sin(", "cos(", "tan(", "ln(", "log("],
		["√(", "x²", "x³", "^", "π"],
		["(", ")", "n!", "e", "÷"],
		["7", "8", "9", "C", "×"],
		["4", "5", "6", "⌫", "−"],
		["1", "2", "3", "%", "+"],
		["0", ".", "±", "Ans", "="],
	];
	const pad = el("div", "rv-calc-pad");
	let ans = "0";
	keys.flat().forEach((label) => {
		const b = el("button", "rv-calc-key"); b.type = "button"; b.textContent = label;
		if (SPOKEN[label]) b.setAttribute("aria-label", SPOKEN[label]);
		if ("=÷×−+".includes(label)) b.classList.add("op");
		if (label === "=") b.classList.add("eq");
		b.addEventListener("click", () => {
			if (label === "C") { display.value = ""; out.textContent = "0"; }
			else if (label === "⌫") { display.value = display.value.slice(0, -1); compute(); }
			else if (label === "=") { compute(); ans = out.textContent; if (ans !== "Error") display.value = ans; }
			else if (label === "Ans") ins(ans);
			else if (label === "√(") ins("sqrt(");
			else if (label === "x²") ins("^2");
			else if (label === "x³") ins("^3");
			else if (label === "n!") ins("!");
			else if (label === "±") { display.value = display.value.startsWith("-") ? display.value.slice(1) : "-" + display.value; compute(); }
			else ins(label);
		});
		pad.appendChild(b);
	});

	const top = el("div", "rv-calc-top");
	top.append(degBtn, out);
	s.append(top, display, pad);
	display.focus();
}
